import type { OwaDate } from '../schema';
import type { IsoDateFields, UTCDateFields } from 'owa-date-subsets';
import { cleanTimeZoneId, CustomTimeZoneId, getTimeZoneOffset } from 'owa-datetime-store';
import { timestamp } from '../timestamp';
import { MILLISECONDS_IN_MINUTE } from 'owa-date-constants';
import parse from './parse';

/**
 * A type that represents a time zone parameter.
 * The value can take the following forms:
 * - The identity of the desired time zone as returned by the service's getTimeZone and getTimeZoneOffsets calls.
 * - The value of getUserConfiguration().UserOptions.TimeZone.
 * - An existing OwaDate or other object with a tz field.
 * - If not specified, it defaults to UTC.
 */
export type TimeZoneParameter = string | { readonly tz: string; readonly assumeOffset?: number };

/** Creates a new OwaDate time with the value of now in UTC. */
export function owaDate(): OwaDate;

/**
 * Creates a new OwaDate.
 *
 * @param timeZone The desired target time zone. If not specified, it defaults to UTC.
 * @param date A value that provides the UTC date of the new date.
 * The value can take the following forms:
 * - An ISO string, such as a string received from EWS.
 * - The number of millisecond since midnight Jan 1 1970, in UTC.
 * - An existing OwaDate which provides the underlying UTC value.
 * - If not specified, it defaults to timestamp().
 *
 * @description
 * This function can be used to create a new OwaDate or to
 * convert an existing date to a different time zone.
 *
 * NOTE: I've removed "Date" as a valid type for the date parameter
 * until we get rid of all DisplayDate usage; otherwise people would
 * be tempted to pass a DisplayDate to owaDate and it would not work
 * as expected. Once we move everything to owaDate we can reintroduce
 * Date and clean-up the calls to getTime() in owaDateMath.
 * Supporting DisplayDate in this call is not a good option because
 * unfortunately there is code that just casts a date to DisplayDate
 * and we would not be able to differentiate it.
 */
export function owaDate(
    timeZone?: TimeZoneParameter | null | undefined,
    date?: string | number | OwaDate | null
): OwaDate;

/**
 * Creates a new OwaDate with the face values of a time-zone specific date & time.
 *
 * @param timeZone The desired target time zone. If not specified, it defaults to UTC.
 * @param fullYear The year of the desired date, in the specified time zone. Required.
 * @param month The month of the desired date, in the specified time zone, starting from 0 (January). Required.
 * @param day The day of the desired date, in the specified time zone. Defaults to 1.
 * @param hours The hours of the desired date, in the specified time zone. Defaults to 0.
 * @param minutes The minutes of the desired date, in the specified time zone. Defaults to 0.
 * @param seconds The seconds of the desired date, in the specified time zone. Defaults to 0.
 * @param milliseconds The milliseconds of the desired date, in the specified time zone. Defaults to 0.
 */
export function owaDate(
    timeZone: TimeZoneParameter | null | undefined,
    fullYear: number,
    month: number,
    day?: number,
    hours?: number,
    minutes?: number,
    seconds?: number,
    milliseconds?: number
): OwaDate;

/** Creates a new OwaDate with the specified time zone and date values. */
export function owaDate(
    timeZone?: TimeZoneParameter | null | undefined,
    ...dateValues:
        | unknown[]
        | [string | number | OwaDate | null]
        | [number, number, number?, number?, number?, number?, number?]
): OwaDate {
    // Default to 'UTC' if no time zone is explicitly specified
    const targetTimeZone = timeZone ?? 'UTC';

    // Get the offset of the given date in the desired target time zone.
    let tz = typeof targetTimeZone == 'string' ? targetTimeZone : targetTimeZone?.tz;
    tz = cleanTimeZoneId(tz);

    // The UTC instant (ms since the epoch) comes from either a 'date' parameter in its different
    // forms or from date/time values considered local to the given time zone, adjusted to the
    // corresponding UTC value.
    let utcMs: number;
    if (dateValues.length <= 1) {
        const date = dateValues[0] ?? timestamp();
        if (typeof date == 'string') {
            return parse(tz, date);
        }
        // `+date` yields a number unchanged, a Date via its time, and an existing OwaDate via its
        // (inherited) valueOf, i.e. its UTC instant.
        utcMs = +date;
    } else {
        // Produce a time-zone local date and adjust to its corresponding UTC
        const [y, mo, d, h, min, s, ms] = dateValues as [
            number,
            number,
            number | undefined,
            number | undefined,
            number | undefined,
            number | undefined,
            number | undefined,
        ];
        const tzDate = new Date(0);
        tzDate.setUTCFullYear(y, mo || 0, d || 1);
        tzDate.setUTCHours(h || 0, min || 0, s || 0, ms || 0);

        const localMs = tzDate.getTime();
        const localOffset = getTimeZoneOffset(localMs, tz, true);
        utcMs = localMs - localOffset * MILLISECONDS_IN_MINUTE;
    }

    // When parsing, we can now assume that an offset found in the string is the correct one
    // for the target time zone; if that happens, we skip the offset lookup and use whatever
    // was found in the string; this way calendarView can show old dates in correct place.
    const assumeOffset = typeof timeZone == 'string' ? undefined : timeZone?.assumeOffset;
    const offset = assumeOffset == undefined ? getTimeZoneOffset(utcMs, tz) : assumeOffset;

    return new OwaDateImpl(utcMs, offset, tz);
}

/** Creates a new OwaDate in the UTC time zone. See owaDate for additional information. */
export const utcDate = owaDate.bind(undefined, 'UTC') as {
    (): OwaDate;
    (date?: string | number | OwaDate | null): OwaDate;
    (
        fullYear: number,
        month: number,
        day?: number,
        hours?: number,
        minutes?: number,
        seconds?: number,
        milliseconds?: number
    ): OwaDate;
};

/** Creates a new OwaDate in the default time zone selected by the OWA user. See owaDate for additional information. */
export const userDate = owaDate.bind(undefined, CustomTimeZoneId) as typeof utcDate;

/**
 * The "adjusted" Date carries the time-zone-local face values in its **UTC** fields. It is typed
 * with the owa-date-subsets helpers (`UTCDateFields` + `IsoDateFields`) so the implementation can
 * only read it through its UTC / ISO accessors — calling a *local* getter (e.g. `getHours`) would
 * leak the host machine's time zone. This is the same contract the old `utc` field used.
 */
type AdjustedDate = UTCDateFields &
    IsoDateFields & {
        // NOTE: OWA returns negative numbers for timezones that are behind UTC. Ex: PST is -480.
        // JavaScript's Date.getTimezoneOffset returns positive numbers. Ex: PST is 480.
        // This field follows the JavaScript model since it is returned by OwaDate.getTimezoneOffset.
        offset: number;
        toString(): string;
    };

/**
 * Time-zone-aware date/time value.
 *
 * `OwaDate extends Date` so instances are `instanceof Date` and can be handed to date-fns
 * directly, and `getTime`/`valueOf`/`getUTC*`/`toISOString` come for free from the underlying
 * `[[DateValue]]` (set to the UTC instant by `super(utcMs)`).
 *
 * Two things make this safe:
 * - **Local accessors are all overridden** so they read the time-zone-local face values (from the
 *   lazily-built `#adjusted` Date) rather than leaking the host machine's time zone.
 * - **`Symbol.toStringTag` is `'Object'`**, so structural-equality consumers (Jest `toEqual`,
 *   MobX `comparer.structural`, lodash `isEqual`, etc.) treat an OwaDate as a plain
 *   (time-zone-sensitive) object and compare its `tz` / `utcMs` / `offset` fields, instead of the
 *   bare-`Date` short-circuit that would compare by timestamp only. It must be `'Object'` and not a
 *   custom tag like `'OwaDate'`: tag-based deep-equal (lodash `isEqual` and friends) treats an
 *   unrecognized tag as *not equal*, so a custom tag makes every OwaDate compare unequal.
 *   `instanceof Date` is unaffected.
 *
 * Instances are read-only: every setter throws.
 *
 * NOTE: do not rely on structured clone / `JSON.stringify` to round-trip an OwaDate. Because it
 * `extends Date`, structured clone yields a plain `Date` (the UTC instant only) and `JSON.stringify`
 * yields the UTC ISO string — the `tz`/`offset` are dropped. To persist/transfer, serialize the ISO
 * string plus `tz` and rebuild with `owaDate(tz, iso)`.
 */
class OwaDateImpl extends Date {
    readonly tz: string;
    /** UTC instant in ms. Mirrors the inherited `[[DateValue]]`; kept as a field so structural equality can see it. */
    readonly utcMs: number;
    /** Offset in minutes, OWA convention (negative behind UTC). */
    readonly offset: number;
    /** Lazily materialized Date whose UTC fields carry the time-zone-local face values. */
    #adjusted: AdjustedDate | undefined;

    constructor(utcMs: number, offset: number, tz: string) {
        super(utcMs);
        // Mirror the (integer-clipped) [[DateValue]] rather than the raw input, so `utcMs` always
        // equals `getTime()`. Structural equality compares `utcMs`, so a non-integer/out-of-range
        // input that clips differently in `[[DateValue]]` must not leave a divergent `utcMs`.
        this.utcMs = this.getTime();
        // Normalize -0 to 0. Time-zone date math can yield a -0 offset (e.g. for UTC, via
        // `-adjusted.offset`), and because `offset` is an own field seen by structural equality,
        // a -0 would make `toEqual` treat two otherwise-identical dates as different.
        this.offset = offset || 0;
        this.tz = tz;
    }

    // Present as a plain object (not a Date) to structural-equality consumers so they compare the
    // tz/utcMs/offset fields instead of short-circuiting on the timestamp. Must be 'Object' (a tag
    // recognized by lodash isEqual & friends); a custom tag makes lodash treat every OwaDate as
    // unequal. `instanceof Date` is unaffected.
    get [Symbol.toStringTag](): string {
        return 'Object';
    }

    /**
     * The Date whose **UTC** fields carry the time-zone-local face values (never a Date's local
     * fields, which depend on the host time zone and cannot represent every wall-clock time, e.g.
     * the nonexistent 2am during a spring-forward DST transition). Built lazily on first use and
     * cached; also read directly by owaDateMath and debug serializers.
     */
    get adjusted(): AdjustedDate {
        let adjusted = this.#adjusted;
        if (adjusted === undefined) {
            adjusted = new Date(
                this.utcMs + this.offset * MILLISECONDS_IN_MINUTE
            ) as unknown as AdjustedDate;
            adjusted.offset = -this.offset || 0;
            adjusted.toString = adjustedToString;
            this.#adjusted = adjusted;
        }
        return adjusted;
    }

    //
    // "local" values come from the adjusted date. DO NOT use arrow functions / the inherited local
    // getters, which would resolve in the host machine's time zone.
    //
    override getFullYear(): number {
        return this.adjusted.getUTCFullYear();
    }
    getYear(): number {
        return this.getFullYear();
    }
    override getMonth(): number {
        return this.adjusted.getUTCMonth();
    }
    override getDate(): number {
        return this.adjusted.getUTCDate();
    }
    override getDay(): number {
        return this.adjusted.getUTCDay();
    }
    override getHours(): number {
        return this.adjusted.getUTCHours();
    }
    override getMinutes(): number {
        return this.adjusted.getUTCMinutes();
    }
    override getSeconds(): number {
        return this.adjusted.getUTCSeconds();
    }
    override getMilliseconds(): number {
        return this.adjusted.getUTCMilliseconds();
    }
    override getTimezoneOffset(): number {
        return -this.offset || 0;
    }

    //
    // UTC values + getTime/valueOf + toISOString/toJSON are INHERITED from Date
    // (backed by the `[[DateValue]]` set in the constructor) and need no override.
    //
    // toUTCString / toGMTString are overridden to return the ISO string, matching the historical
    // OwaDate behavior, rather than Date's RFC-1123 format ("Tue, 02 Jan 2018 03:04:05 GMT").
    //
    override toUTCString(): string {
        return this.toISOString();
    }
    toGMTString(): string {
        return this.toISOString();
    }

    //
    // String forms render the time-zone-local value.
    //
    override toString(): string {
        return this.adjusted.toString();
    }
    override toDateString(): string {
        return this.toString().replace(/T.*/, '');
    }
    override toTimeString(): string {
        return this.toString().replace(/.*T/, '');
    }
    override toLocaleDateString(): string {
        throw new Error('formatUserDate should be called directly');
    }
    override toLocaleTimeString(): string {
        throw new Error('formatUserTime should be called directly');
    }
    override toLocaleString(): string {
        throw new Error('formatUserDateTime should be called directly');
    }

    //
    // OwaDate instances are read-only: setters keep Date's parameter lists but always throw.
    //
    override setTime(_time: number): never {
        throwReadonly();
    }
    override setFullYear(_year: number, _month?: number, _date?: number): never {
        throwReadonly();
    }
    setYear(_year: number): never {
        throwReadonly();
    }
    override setMonth(_month: number, _date?: number): never {
        throwReadonly();
    }
    override setDate(_date: number): never {
        throwReadonly();
    }
    override setHours(_hours: number, _min?: number, _sec?: number, _ms?: number): never {
        throwReadonly();
    }
    override setMinutes(_min: number, _sec?: number, _ms?: number): never {
        throwReadonly();
    }
    override setSeconds(_sec: number, _ms?: number): never {
        throwReadonly();
    }
    override setMilliseconds(_ms: number): never {
        throwReadonly();
    }
    override setUTCFullYear(_year: number, _month?: number, _date?: number): never {
        throwReadonly();
    }
    override setUTCMonth(_month: number, _date?: number): never {
        throwReadonly();
    }
    override setUTCDate(_date: number): never {
        throwReadonly();
    }
    override setUTCHours(_hours: number, _min?: number, _sec?: number, _ms?: number): never {
        throwReadonly();
    }
    override setUTCMinutes(_min: number, _sec?: number, _ms?: number): never {
        throwReadonly();
    }
    override setUTCSeconds(_sec: number, _ms?: number): never {
        throwReadonly();
    }
    override setUTCMilliseconds(_ms: number): never {
        throwReadonly();
    }
}

function throwReadonly(): never {
    throw new Error('OwaDate is read-only.');
}

function adjustedToString(this: AdjustedDate) {
    // See AdjustedDate.offset for more details.
    return this.toISOString().replace('Z', offsetStr(this.offset));
}

function offsetStr(offset: number) {
    if (offset == 0) {
        return 'Z';
    }
    const tzHour = Math.floor(Math.abs(offset / 60));
    const tzMinutes = Math.abs(offset % 60);
    return (offset > 0 ? '-' : '+') + pad(tzHour) + ':' + pad(tzMinutes);
}

function pad(absValue: number) {
    const str = absValue.toString(10);
    return absValue < 10 ? '0' + str : str;
}
